import {
  addDays,
  addYears,
  differenceInCalendarDays,
  endOfDay,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  isWithinInterval,
  startOfDay,
  startOfTomorrow,
  subDays,
} from 'date-fns';
import { date as dateUtil, string } from '@eti/utils';
import { tripTypes } from '../../../constants/tripTypesConstants';
import { DEPARTURE_DATE, RETURN_DATE } from '../constants/formFields';

const { MULTI_STOP, RETURN } = tripTypes;

type DayValue = Date | null | undefined;

type DateMask = {
  from?: DayValue;
  to?: DayValue;
};

export const getStartOfToday = () => startOfDay(new Date());

export const getStartOfTomorrow = () => startOfTomorrow();

export const getOneYearAndOneDayFromToday = () => addDays(addYears(getStartOfToday(), 1), 1);

export const getOneYearFromToday = () => addYears(getStartOfToday(), 1);

const getDaysFromToday = (days: number) => addDays(getStartOfToday(), days);

export const isSameDate = (day1: DayValue, day2: DayValue) =>
  Boolean(day1 && day2 && isSameDay(day1, day2));

export const isDayBefore = (day1: DayValue, day2: DayValue) => {
  if (!day1 || !day2) {
    return false;
  }
  return isBefore(day1, day2);
};

export const isDayAfter = (day1: DayValue, day2: DayValue) => {
  if (!day1 || !day2) {
    return false;
  }
  return isAfter(day1, day2);
};

export function isDayInRange(day: DayValue, { from, to }: { from: Date; to: Date }) {
  if (!day) {
    return false;
  }

  const start = startOfDay(from);
  const end = endOfDay(to);

  try {
    return isWithinInterval(day, { start, end });
  } catch {
    return false;
  }
}

export const isSelected = (day: DayValue, departureDate: DayValue, returnDate: DayValue) =>
  isSameDate(day, departureDate) || isSameDate(day, returnDate);

export const isInRange = (
  day: DayValue,
  departureDate: DayValue,
  returnDate: DayValue,
  mask: DateMask,
) => {
  if (!departureDate || !returnDate || !day) {
    return false;
  }

  const range = {
    from: addDays(departureDate, 1),
    to: subDays(returnDate, 1),
  };
  return isDayInRange(day, range) && (!mask.from || !mask.to);
};

const getStartOfValidRange = (
  day: DayValue,
  numberOfDaysFromToday: number,
  selectedTripType: string | null | undefined,
) => (selectedTripType === MULTI_STOP && day ? day : getDaysFromToday(numberOfDaysFromToday));

export const isDisabled = (
  day: DayValue,
  numberOfDaysFromToday = 0,
  previousBoundDepartureDate: DayValue,
  selectedTripType: string | null | undefined,
  minSelectableDate: DayValue,
  maxSelectableDate: DayValue,
) => {
  if (!day) {
    return false;
  }

  const validRange: { from: Date; to: Date } = {
    from:
      minSelectableDate ??
      getStartOfValidRange(previousBoundDepartureDate, numberOfDaysFromToday, selectedTripType),
    to: maxSelectableDate ?? getOneYearFromToday(),
  };

  return !isDayInRange(day, validRange);
};

export const isMasked = (
  date: DayValue,
  selectedTripType: string | null | undefined,
  start: DayValue,
  end: DayValue,
) => {
  if (
    selectedTripType !== RETURN ||
    !date ||
    !start ||
    !end ||
    !isValid(start) ||
    !isValid(end) ||
    isSameDay(date, start) ||
    isSameDay(date, end)
  ) {
    return false;
  }

  return isWithinInterval(date, { start, end });
};

const dateMask = (from: DayValue, to: DayValue): DateMask => ({
  from,
  to,
});

export const calculateMask = (
  currentField: string | null | undefined,
  departureDate: DayValue,
  returnDate: DayValue,
  day: DayValue,
): DateMask => {
  if (currentField === DEPARTURE_DATE) {
    if (isDayBefore(day, returnDate)) {
      return dateMask(day, returnDate);
    }
    if (isSameDate(day, returnDate)) {
      return dateMask(day, day);
    }
    if (isDayAfter(day, returnDate)) {
      return dateMask(day, null);
    }
  } else if (currentField === RETURN_DATE) {
    if (isDayAfter(day, departureDate)) {
      return dateMask(departureDate, day);
    }
    if (isSameDate(day, departureDate)) {
      return dateMask(day, day);
    }
    if (isDayBefore(day, departureDate)) {
      return dateMask(null, day);
    }
  }

  return dateMask(departureDate, returnDate);
};

export const isDepartureDateValid = (day: DayValue, returnDate: DayValue) =>
  Boolean(returnDate && isDayBefore(day, returnDate));

export const isReturnDateValid = (day: DayValue, departureDate: DayValue) =>
  Boolean(departureDate && (isSameDate(day, departureDate) || isDayAfter(day, departureDate)));

// Overloaded because callers wire this up to either a Date-based or a (legacy) string-based
// setter pair - never a mix of the two. The implementation signature below has to widen to
// cover both so it can forward `day` to whichever pair was actually passed in.
export function calculateDayRange(
  currentField: string | null | undefined,
  departureDate: DayValue,
  returnDate: DayValue,
  day: Date,
  setDepartureDate: (day: Date) => void,
  setReturnDate: (day: Date) => void,
): void;
export function calculateDayRange(
  currentField: string | null | undefined,
  departureDate: DayValue,
  returnDate: DayValue,
  day: Date,
  setDepartureDate: (day: string) => void,
  setReturnDate: (day: string) => void,
): void;
export function calculateDayRange(
  currentField: string | null | undefined,
  departureDate: DayValue,
  returnDate: DayValue,
  day: Date,
  setDepartureDate: ((day: Date) => void) | ((day: string) => void) | undefined,
  setReturnDate: ((day: Date) => void) | ((day: string) => void) | undefined,
): void;
export function calculateDayRange(
  currentField: string | null | undefined,
  departureDate: DayValue,
  returnDate: DayValue,
  day: Date,
  setDepartureDate: ((day: Date) => void) | ((day: string) => void) | undefined,
  setReturnDate: ((day: Date) => void) | ((day: string) => void) | undefined,
) {
  const setDeparture = setDepartureDate as (day: Date | string | undefined) => void;
  const setReturn = setReturnDate as (day: Date | string | undefined) => void;

  if (currentField === DEPARTURE_DATE) {
    if (isDayAfter(day, returnDate)) {
      setReturn(undefined);
    }
    setDeparture(day);
    return;
  }

  if (currentField === RETURN_DATE) {
    if (isDayBefore(day, departureDate)) {
      setDeparture(day);
      setReturn(undefined);
      return;
    }
    setReturn(day);
  }
}

export const generateDurationText = (
  from: Date | string | null | undefined,
  to: Date | string | null | undefined,
  durationTextSingular: string,
  durationTextPlural: string,
) => {
  const fromDate = from ? dateUtil.convertToDate(from) : undefined;
  const toDate = to ? dateUtil.convertToDate(to) : undefined;
  const duration =
    fromDate && toDate && !isSameDate(fromDate, toDate)
      ? differenceInCalendarDays(toDate, fromDate) + 1
      : 1;
  const durationText = duration > 1 ? durationTextPlural : durationTextSingular;

  return string.insertArgument(durationText, duration);
};
